// src/components/RecipeForm.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Collapse,
  Space,
  Row,
  Col,
  Typography,
  Divider,
  InputNumber,
  message,
  Spin,
  Alert,
  Empty,
  Tag,
  Badge,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  ExperimentOutlined,
  ApiOutlined,
  LinkOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  CopyOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import ReactAce from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-cobalt';
import 'ace-builds/src-noconflict/theme-github';
import apiClient from '../services/axiosConfig';
import { useTheme } from '../App';
import _ from 'lodash';

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Magic template generator
const generateMagicTemplate = (apiConfig) => {
  const template = {};
  
  // Generate sample values based on data type
  const getSampleValue = (dataType, fieldName, enumValues) => {
    switch (dataType) {
      case 'string':
        return `sample_${fieldName}`;
      case 'number':
        return 123;
      case 'boolean':
        return true;
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'enum':
        return enumValues && enumValues.length > 0 ? enumValues[0] : 'enum_value';
      case 'array':
        return [];
      case 'object':
        return {};
      default:
        return null;
    }
  };

  // Process object schema recursively
  const processObjectSchema = (schema) => {
    const obj = {};
    schema.forEach(field => {
      if (field.fieldType === 'object' && field.objectSchema) {
        obj[field.fieldName] = processObjectSchema(field.objectSchema);
      } else if (field.fieldType === 'array') {
        if (field.arrayItemType === 'object' && field.objectSchema) {
          obj[field.fieldName] = [processObjectSchema(field.objectSchema)];
        } else {
          obj[field.fieldName] = [getSampleValue(field.arrayItemType, field.fieldName)];
        }
      } else {
        obj[field.fieldName] = getSampleValue(field.fieldType, field.fieldName, field.enumValues);
      }
    });
    return obj;
  };

  // Process parameters by type
  if (apiConfig.parameters) {
    apiConfig.parameters.forEach(param => {
      const paramType = param.parameterType;
      
      // Initialize section if not exists
      if (!template[paramType]) {
        template[paramType] = {};
      }
      
      // Handle different data types
      if (param.dataType === 'object' && param.objectSchema) {
        template[paramType][param.name] = processObjectSchema(param.objectSchema);
      } else if (param.dataType === 'array') {
        if (param.arrayItemType === 'object' && param.objectSchema) {
          template[paramType][param.name] = [processObjectSchema(param.objectSchema)];
        } else if (param.arrayItemType === 'enum' && param.enumValues) {
          template[paramType][param.name] = param.enumValues;
        } else {
          template[paramType][param.name] = [getSampleValue(param.arrayItemType, param.name)];
        }
      } else {
        template[paramType][param.name] = getSampleValue(param.dataType, param.name, param.enumValues);
      }
    });
  }

  // Handle attributes
  if (apiConfig.attributes && apiConfig.attributes.length > 0) {
    template.attributes = apiConfig.attributes.map(attr => attr.name);
  }

  // Clean up empty sections
  Object.keys(template).forEach(key => {
    if (Object.keys(template[key]).length === 0) {
      delete template[key];
    }
  });

  // Rename 'header' to 'headers' for consistency
  if (template.header) {
    template.headers = template.header;
    delete template.header;
  }

  return template;
};

// Component for step configuration with JSON editor
const StepConfiguration = ({ selectedApi, stepIndex, initialOverrides = {}, onChange }) => {
  const { theme, currentTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('body');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // State for each parameter type - initialize with initial values
  const [queryJson, setQueryJson] = useState(() => 
    JSON.stringify(initialOverrides.query || {}, null, 2)
  );
  const [pathJson, setPathJson] = useState(() => 
    JSON.stringify(initialOverrides.path || {}, null, 2)
  );
  const [headersJson, setHeadersJson] = useState(() => 
    JSON.stringify(initialOverrides.headers || {}, null, 2)
  );
  const [bodyJson, setBodyJson] = useState(() => 
    JSON.stringify(initialOverrides.body || {}, null, 2)
  );
  const [attributesJson, setAttributesJson] = useState(() => 
    JSON.stringify(initialOverrides.attributes || [], null, 2)
  );

  // Mark as initialized after first render
  useEffect(() => {
    setHasInitialized(true);
  }, []);

  // Debounced update to parent
  const updateParent = useMemo(
    () => _.debounce((stepIdx, qJson, pJson, hJson, bJson, aJson) => {
      try {
        const overrides = {};
        
        const query = JSON.parse(qJson);
        if (Object.keys(query).length > 0) overrides.query = query;
        
        const path = JSON.parse(pJson);
        if (Object.keys(path).length > 0) overrides.path = path;
        
        const headers = JSON.parse(hJson);
        if (Object.keys(headers).length > 0) overrides.headers = headers;
        
        const body = JSON.parse(bJson);
        if (Object.keys(body).length > 0) overrides.body = body;
        
        const attributes = JSON.parse(aJson);
        if (Array.isArray(attributes) && attributes.length > 0) overrides.attributes = attributes;
        
        onChange(stepIdx, overrides);
      } catch (e) {
        // Invalid JSON, don't update
      }
    }, 500),
    [onChange]
  );

  // Update parent when any JSON changes (only after initialization)
  useEffect(() => {
    if (hasInitialized) {
      updateParent(stepIndex, queryJson, pathJson, headersJson, bodyJson, attributesJson);
    }
  }, [queryJson, pathJson, headersJson, bodyJson, attributesJson, stepIndex, updateParent, hasInitialized]);

  const handleMagicCreate = () => {
    if (!selectedApi) {
      message.warning('Please select an API first');
      return;
    }

    const template = generateMagicTemplate(selectedApi);
    
    // Set the generated values
    setQueryJson(JSON.stringify(template.query || {}, null, 2));
    setPathJson(JSON.stringify(template.path || {}, null, 2));
    setHeadersJson(JSON.stringify(template.headers || {}, null, 2));
    setBodyJson(JSON.stringify(template.body || {}, null, 2));
    setAttributesJson(JSON.stringify(template.attributes || [], null, 2));
    
    message.success('Magic template created! Edit the values as needed.');
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    message.success('Copied to clipboard');
  };

  const renderJsonEditor = (value, onChange, paramType) => {
    const hasContent = value !== '{}' && value !== '[]' && value.length > 4;
    
    return (
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 8
        }}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(value)}
            disabled={!hasContent}
          >
            Copy
          </Button>
        </div>
        
        <ReactAce
          mode="json"
          theme={currentTheme === 'dark' ? 'cobalt' : 'github'}
          name={`${paramType}_editor_${stepIndex}_${Date.now()}`}
          onChange={onChange}
          fontSize={14}
          showPrintMargin={true}
          showGutter={true}
          highlightActiveLine={true}
          value={value}
          width="100%"
          height={isExpanded ? "400px" : "200px"}
          setOptions={{
            showLineNumbers: true,
            tabSize: 2,
            useWorker: false,
            behavioursEnabled: true,
            wrapBehavioursEnabled: true,
          }}
          style={{
            borderRadius: '8px',
            border: `1px solid ${theme.border}`,
          }}
          editorProps={{ $blockScrolling: true }}
        />
      </div>
    );
  };

  // Get parameter info for the selected API
  const getParamInfo = (paramType) => {
    if (!selectedApi || !selectedApi.parameters) return [];
    
    return selectedApi.parameters
      .filter(p => p.parameterType === paramType)
      .map(p => ({
        name: p.name,
        type: p.dataType,
        required: p.isRequired,
        description: p.description,
        enumValues: p.enumValues
      }));
  };

  const renderParamHelper = (paramType) => {
    const params = getParamInfo(paramType);
    if (params.length === 0) return null;
    
    return (
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Available fields: {' '}
          {params.map((p, idx) => (
            <span key={p.name}>
              <Tag 
                size="small" 
                color={p.required ? 'red' : 'default'}
                style={{ fontSize: 11 }}
              >
                {p.name} ({p.type}){p.required && '*'}
              </Tag>
              {idx < params.length - 1 && ' '}
            </span>
          ))}
        </Text>
      </div>
    );
  };

  if (!selectedApi) {
    return (
      <Alert
        message="Select an API endpoint above to configure its parameters"
        type="info"
        showIcon
      />
    );
  }

  return (
    <div>
      <Alert
        message="Configure request parameters"
        description="Use the JSON editors below to set values for this API call. Click 'Magic Create' to generate a template."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          icon={<ThunderboltOutlined />}
          onClick={handleMagicCreate}
          type="primary"
          ghost
        >
          Magic Create
        </Button>
        <Button
          icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><CodeOutlined /> Body</span>} key="body">
          {renderParamHelper('body')}
          {renderJsonEditor(bodyJson, setBodyJson, 'body')}
        </TabPane>
        
        <TabPane tab={<span><CodeOutlined /> Query</span>} key="query">
          {renderParamHelper('query')}
          {renderJsonEditor(queryJson, setQueryJson, 'query')}
        </TabPane>
        
        <TabPane tab={<span><CodeOutlined /> Path</span>} key="path">
          {renderParamHelper('path')}
          {renderJsonEditor(pathJson, setPathJson, 'path')}
        </TabPane>
        
        <TabPane tab={<span><CodeOutlined /> Headers</span>} key="headers">
          {renderParamHelper('header')}
          {renderJsonEditor(headersJson, setHeadersJson, 'headers')}
        </TabPane>
        
        <TabPane tab={<span><CodeOutlined /> Attributes</span>} key="attributes">
          {selectedApi.attributes && selectedApi.attributes.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Available attributes: {' '}
                {selectedApi.attributes.map((attr, idx) => (
                  <span key={attr.name}>
                    <Tag size="small" style={{ fontSize: 11 }}>
                      {attr.name} ({attr.responseType})
                    </Tag>
                    {idx < selectedApi.attributes.length - 1 && ' '}
                  </span>
                ))}
              </Text>
            </div>
          ) : (
            <Alert
              message="No attributes available for this API"
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}
          {renderJsonEditor(attributesJson, setAttributesJson, 'attributes')}
        </TabPane>
      </Tabs>
    </div>
  );
};

function RecipeForm({ initialValues = {}, isEditMode = false, onSubmit, onCancel }) {
  const { theme, currentTheme } = useTheme();
  const [form] = Form.useForm();
  const [apiConfigs, setApiConfigs] = useState([]);
  const [loadingApis, setLoadingApis] = useState(false);
  const [stepApiSelections, setStepApiSelections] = useState({});
  const [stepOverrides, setStepOverrides] = useState({});

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Initialize form with initial values
  useEffect(() => {
    if (initialValues && initialValues.steps && apiConfigs.length > 0) {
      const processedValues = {
        ...initialValues,
        steps: initialValues.steps.map((step, idx) => ({
          ...step,
          stepNumber: step.stepNumber || idx + 1
        }))
      };
      
      form.setFieldsValue(processedValues);
      
      const selections = {};
      const overrides = {};
      
      initialValues.steps.forEach((step, idx) => {
        if (step.apiConfigId) {
          const apiId = typeof step.apiConfigId === 'object' ? step.apiConfigId._id : step.apiConfigId;
          selections[idx] = apiId;
        }
        if (step.requestOverrides) {
          overrides[idx] = step.requestOverrides;
        }
      });
      
      setStepApiSelections(selections);
      setStepOverrides(overrides);
    }
  }, [initialValues, form, apiConfigs]);

  useEffect(() => {
    fetchApiConfigs();
  }, []);

  const fetchApiConfigs = async () => {
    setLoadingApis(true);
    try {
      const res = await apiClient.get(`${API_BASE_URL}/config`, {
        params: { includeDetails: true }
      });
      const { data } = res.data;
      setApiConfigs(data || []);
    } catch (err) {
      console.error('Failed to fetch API configs:', err);
      message.error('Could not load API configs');
    } finally {
      setLoadingApis(false);
    }
  };
// Replace the existing getAvailableFields function with this:
const getAvailableFields = (apiConfig) => {
  const fields = [];
  
  if (apiConfig?.parameters) {
    apiConfig.parameters.forEach(param => {
      const prefix = param.parameterType === 'header' ? 'headers' : param.parameterType;
      
      // Handle array parameters
      if (param.dataType === 'array') {
        // Add the base array field
        fields.push({
          value: `${prefix}.${param.name}`,
          label: `${prefix}.${param.name} (entire array)`,
          type: 'array'
        });
        
        // For arrays, show first 5 indices by default
        const ARRAY_INDICES = 5;
        
        if (param.arrayItemType === 'object' && param.objectSchema) {
          // For array of objects, add fields for each index
          for (let i = 0; i < ARRAY_INDICES; i++) {
            param.objectSchema.forEach(field => {
              fields.push({
                value: `${prefix}.${param.name}[${i}].${field.fieldName}`,
                label: `${prefix}.${param.name}[${i}].${field.fieldName}`,
                type: field.fieldType || field.dataType
              });
            });
          }
        } else {
          // For simple arrays
          for (let i = 0; i < ARRAY_INDICES; i++) {
            fields.push({
              value: `${prefix}.${param.name}[${i}]`,
              label: `${prefix}.${param.name}[${i}]`,
              type: param.arrayItemType
            });
          }
        }
      } else if (param.dataType === 'object' && param.objectSchema) {
        // Handle nested objects (existing code)
        const addNestedFields = (schema, parentPath) => {
          schema.forEach(field => {
            const fieldPath = parentPath ? `${parentPath}.${field.fieldName}` : field.fieldName;
            fields.push({
              value: `${prefix}.${fieldPath}`,
              label: `${prefix}.${fieldPath}`,
              type: field.fieldType || field.dataType
            });
            
            if (field.fieldType === 'object' && field.objectSchema) {
              addNestedFields(field.objectSchema, fieldPath);
            }
          });
        };
        addNestedFields(param.objectSchema, param.name);
      } else {
        // Simple fields
        fields.push({
          value: `${prefix}.${param.name}`,
          label: `${prefix}.${param.name}`,
          type: param.dataType
        });
      }
    });
  }
  
  // Add special response paths
  fields.push(
    { value: 'response', label: 'response (full response)', type: 'object' },
    { value: 'response.data', label: 'response.data', type: 'object' },
    { value: 'response.status', label: 'response.status', type: 'number' }
  );
  
  return fields;
};
  const handleApiSelection = (stepIndex, apiId) => {
    setStepApiSelections(prev => ({
      ...prev,
      [stepIndex]: apiId
    }));
  };

  const handleOverrideChange = useCallback((stepIndex, overrides) => {
    setStepOverrides(prev => ({
      ...prev,
      [stepIndex]: overrides
    }));
  }, []);

  const handleFinish = async (values) => {
    try {
      const processedSteps = values.steps.map((step, idx) => {
        const stepData = {
          stepNumber: step.stepNumber || idx + 1,
          apiConfigId: step.apiConfigId,
          requestOverrides: stepOverrides[idx] || {},
          dynamicMappings: step.dynamicMappings || []
        };

        return stepData;
      });

      const finalValues = {
        ...values,
        steps: processedSteps
      };

      console.log('🚀 Final recipe data:', finalValues);
      onSubmit(finalValues);
    } catch (error) {
      console.error('Error processing recipe:', error);
      message.error(error.message || 'Failed to save recipe');
    }
  };

  const removeStep = (remove, name, idx) => {
    remove(name);
    
    // Clean up selections and overrides
    setStepApiSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[idx];
      // Shift indices
      const updatedSelections = {};
      Object.keys(newSelections).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > idx) {
          updatedSelections[keyNum - 1] = newSelections[key];
        } else {
          updatedSelections[key] = newSelections[key];
        }
      });
      return updatedSelections;
    });
    
    setStepOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[idx];
      // Shift indices
      const updatedOverrides = {};
      Object.keys(newOverrides).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > idx) {
          updatedOverrides[keyNum - 1] = newOverrides[key];
        } else {
          updatedOverrides[key] = newOverrides[key];
        }
      });
      return updatedOverrides;
    });
  };

  if (loadingApis) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading API configurations...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="recipe-form">
      <Row gutter={16} style={{ padding: '20px', backgroundColor: theme.background, minHeight: '100vh' }}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ExperimentOutlined style={{ fontSize: 24, color: theme.primary }} />
                <span>{isEditMode ? 'Edit Recipe' : 'Create Recipe'}</span>
              </div>
            }
            bordered={false}
            style={{ 
              boxShadow: currentTheme === 'dark' ? '0 1px 4px rgba(255,255,255,0.1)' : '0 1px 4px rgba(0,0,0,0.1)',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              preserve={true}
            >
              <Title level={4} style={{ marginBottom: 16, color: theme.text }}>
                Recipe Information
              </Title>

              <Form.Item
                name="name"
                label="Recipe Name"
                rules={[{ required: true, message: 'Please enter a recipe name' }]}
              >
                <Input placeholder="e.g. Competition Flow" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <Input.TextArea
                  placeholder="Describe what this recipe does..."
                  autoSize={{ minRows: 2, maxRows: 5 }}
                />
              </Form.Item>

              <Form.Item name="tags" label="Tags (optional)">
                <Select
                  mode="tags"
                  placeholder="Add tags to categorize this recipe"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Divider />

              <Title level={4} style={{ marginBottom: 16, color: theme.text }}>
                Steps
              </Title>

              <Form.List name="steps">
                {(fields, { add, remove }) => (
                  <>
                    {fields.length === 0 && (
                      <Card style={{ 
                        marginBottom: 16, 
                        backgroundColor: theme.surfaceLight,
                        border: `1px solid ${theme.border}`,
                        textAlign: 'center',
                        padding: '40px'
                      }}>
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <span style={{ color: theme.textSecondary }}>
                              No steps added yet. Click <strong style={{ color: theme.text }}>"Add Step"</strong> below.
                            </span>
                          }
                        />
                      </Card>
                    )}
                    
                    {fields.map(({ key, name, ...restField }, idx) => {
                      const selectedApiId = stepApiSelections[idx] || form.getFieldValue(['steps', idx, 'apiConfigId']);
                      const selectedApi = apiConfigs.find(cfg => cfg._id === selectedApiId);

                      return (
                        <Card
                          key={key}
                          style={{ 
                            marginBottom: 16, 
                            backgroundColor: theme.surface,
                            border: `1px solid ${theme.border}`
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={5} style={{ marginBottom: 0, color: theme.text }}>
                              <Badge count={idx + 1} style={{ backgroundColor: theme.primary, marginRight: 12 }} />
                              Step {idx + 1}
                            </Title>
                            <Button
                              type="text"
                              danger
                              onClick={() => removeStep(remove, name, idx)}
                              icon={<DeleteOutlined />}
                            >
                              Remove
                            </Button>
                          </div>
                          
                          <Divider style={{ margin: '12px 0' }} />

                          <Row gutter={16}>
                            <Col span={6}>
                              <Form.Item
                                {...restField}
                                name={[name, 'stepNumber']}
                                label="Step Number"
                                rules={[{ required: true, message: 'Step number is required' }]}
                                initialValue={idx + 1}
                              >
                                <InputNumber min={1} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>

                            <Col span={18}>
                              <Form.Item
                                {...restField}
                                label="API Endpoint"
                                name={[name, 'apiConfigId']}
                                rules={[{ required: true, message: 'Select an API endpoint' }]}
                              >
                                <Select
                                  placeholder="Select an API for this step"
                                  showSearch
                                  optionFilterProp="children"
                                  onChange={(value) => handleApiSelection(idx, value)}
                                  filterOption={(input, option) =>
                                    option?.children
                                      ?.toString()
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                >
                                  {apiConfigs.map((cfg) => (
                                    <Option key={cfg._id} value={cfg._id}>
                                      {cfg.group} / {cfg.name} - 
                                      <Tag
                                        color={
                                          cfg.method === 'GET' ? 'green' :
                                          cfg.method === 'POST' ? 'blue' :
                                          cfg.method === 'PUT' ? 'orange' :
                                          cfg.method === 'DELETE' ? 'red' : 'default'
                                        }
                                        style={{ marginLeft: 8 }}
                                      >
                                        {cfg.method} {cfg.route}
                                      </Tag>
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Collapse 
                            style={{ marginTop: 16 }} 
                            bordered={false} 
                            defaultActiveKey={['params']}
                          >
                            <Panel 
                              header={
                                <span>
                                  <ApiOutlined style={{ marginRight: 8 }} />
                                  Configure Request Parameters
                                </span>
                              } 
                              key="params"
                            >
                              <StepConfiguration
                                key={`${selectedApiId}-${idx}`}
                                selectedApi={selectedApi}
                                stepIndex={idx}
                                initialOverrides={stepOverrides[idx]}
                                onChange={handleOverrideChange}
                              />
                            </Panel>

                            <Panel 
                              header={
                                <span>
                                  <LinkOutlined style={{ marginRight: 8 }} />
                                  Dynamic Mappings
                                </span>
                              } 
                              key="mappings"
                            >
                              <Alert
                                message="Map data from previous step responses"
                                description="Use dynamic mappings to pass data from one step's response to another step's request."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                              />
                              
                              <Form.List name={[name, 'dynamicMappings']}>
                                {(mapFields, { add: addMap, remove: removeMap }) => (
                                  <>
                                    {mapFields.map(({ key: mapKey, name: mapName, ...restMap }) => (
                                      <Row
                                        key={mapKey}
                                        gutter={8}
                                        align="middle"
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Col span={6}>
                                          <Form.Item
                                            {...restMap}
                                            label="From Step"
                                            name={[mapName, 'fromStep']}
                                            rules={[{ required: true, message: 'Specify source step' }]}
                                          >
                                            <InputNumber
                                              placeholder="Step #"
                                              style={{ width: '100%' }}
                                              min={1}
                                              max={idx}
                                            />
                                          </Form.Item>
                                        </Col>

                                        <Col span={8}>
                                          <Form.Item
                                            {...restMap}
                                            label="Response Path"
                                            name={[mapName, 'fromPath']}
                                            rules={[{ required: true, message: 'Specify path' }]}
                                            tooltip="e.g. data.id, response.userId"
                                          >
                                            <Input placeholder="response.data.id" />
                                          </Form.Item>
                                        </Col>

                                        <Col span={8}>
                                          <Form.Item
                                            {...restMap}
                                            label="Target Field"
                                            name={[mapName, 'to']}
                                            rules={[{ required: true, message: 'Specify target' }]}
                                          >
                                            <Select
                                              placeholder="Select target field"
                                              showSearch
                                              optionFilterProp="children"
                                            >
                                              {selectedApi && getAvailableFields(selectedApi).map(field => (
                                                <Option key={field.value} value={field.value}>
                                                  <span>
                                                    {field.label}
                                                    <Tag 
                                                      size="small" 
                                                      style={{ marginLeft: 8 }}
                                                      color={
                                                        field.type === 'string' ? 'blue' :
                                                        field.type === 'number' ? 'green' :
                                                        field.type === 'boolean' ? 'orange' :
                                                        field.type === 'array' ? 'purple' :
                                                        field.type === 'object' ? 'red' :
                                                        'default'
                                                      }
                                                    >
                                                      {field.type}
                                                    </Tag>
                                                  </span>
                                                </Option>
                                              ))}
                                            </Select>
                                          </Form.Item>
                                        </Col>

                                        <Col span={2}>
                                          <Button
                                            type="text"
                                            icon={<MinusCircleOutlined />}
                                            danger
                                            onClick={() => removeMap(mapName)}
                                            style={{ marginTop: 28 }}
                                          />
                                        </Col>
                                      </Row>
                                    ))}

                                    <Form.Item>
                                      <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={() => addMap()}
                                      >
                                        Add Mapping
                                      </Button>
                                    </Form.Item>
                                  </>
                                )}
                              </Form.List>
                            </Panel>
                          </Collapse>
                        </Card>
                      );
                    })}

                    <Form.Item>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add({ stepNumber: fields.length + 1 })}
                        style={{ width: '100%' }}
                      >
                        Add Step
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              <Divider />

              <Space style={{ marginTop: 16 }}>
                <Button type="primary" htmlType="submit">
                  {isEditMode ? 'Update Recipe' : 'Save Recipe'}
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
              </Space>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default RecipeForm;